import { Tag } from "lucide-react";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";

export type LabelOption = {
  id: string;
  name: string;
  color: string | null;
  /** Contextual lead count. Omitted where no meaningful count exists. */
  count?: number;
};

type Props = {
  options: LabelOption[];
  selected: string[];
  onToggle: (labelId: string) => void;
  onClear: () => void;
  className?: string;
};

function swatchClass(color: string | null): string {
  return (
    PIPELINE_COLOR_OPTIONS.find((c) => c.name === color)?.swatch ?? "bg-ink-300"
  );
}

/**
 * Multi-select label dropdown. A lead matches if it carries ANY of the selected
 * labels (OR).
 *
 * Presentation only — it owns no data source and no filter state. Two surfaces
 * bind to it with different ones: the pipeline filter bar (crm store + counts
 * scoped to its other filters) and the Buzón (local state + the plain label
 * list). Splitting it this way is what keeps a single copy of the markup without
 * the Buzón firing the pipeline's count query it would never display.
 *
 * Renders nothing when the client has no labels, so a school that never made one
 * sees no dead control.
 */
export function LabelPicker({
  options,
  selected,
  onToggle,
  onClear,
  className,
}: Props): React.JSX.Element | null {
  if (options.length === 0) return null;

  return (
    <MultiSelectFilter
      options={options.map((o) => ({
        id: o.id,
        name: o.name,
        swatchClass: swatchClass(o.color),
        count: o.count,
      }))}
      selected={selected}
      onToggle={onToggle}
      onClear={onClear}
      placeholder="Etiquetas"
      pluralNoun="etiquetas"
      singularNoun="etiqueta"
      clearLabel="Limpiar etiquetas"
      icon={Tag}
      className={className}
    />
  );
}
